import {
    GraduationCap, CreditCard, Code, Share2, ShoppingBag, Layers, Calendar, ShieldCheck,
} from "lucide-react";

const SIGNAL_GROUPS = {
    academic: { label: "Academic", icon: GraduationCap, color: "#3b82f6", providers: ["academic"] },
    fee_payment: { label: "Fee Payment", icon: CreditCard, color: "#06b6d4", providers: ["fee_payment"] },
    developer: { label: "Developer / Professional", icon: Code, color: "#8b5cf6", providers: ["github", "linkedin", "portfolio", "stackoverflow"] },
    social: { label: "Social / Creator", icon: Share2, color: "#f59e0b", providers: ["youtube", "twitter", "x", "instagram", "reddit", "tiktok"] },
    consumption: { label: "Consumption", icon: ShoppingBag, color: "#fb923c", providers: ["ecom_consumption", "ecom"] },
    identity: { label: "Identity / Other", icon: Layers, color: "#10b981", providers: [] },
};

const SOURCE_BADGES = {
    oauth: { label: "OAuth", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" },
    verified_system: { label: "Verified System", color: "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300" },
    uploaded_document: { label: "Uploaded Document", color: "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300" },
    url_ingest: { label: "URL Extracted", color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-500/20 dark:text-cyan-300" },
    user_declared: { label: "Self-declared", color: "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-slate-300" },
    manual: { label: "Self-declared", color: "bg-slate-100 text-slate-700 dark:bg-white/10 dark:text-slate-300" },
    account_aggregator: { label: "Account Aggregator", color: "bg-violet-100 text-violet-700 dark:bg-violet-500/20 dark:text-violet-300" },
    mock: { label: "Mock Data", color: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300" },
};

// Friendly labels for known data keys.
const KEY_LABELS = {
    cgpa: "CGPA",
    scale: "Grade Scale",
    institution: "Institution",
    institution_tier: "Institution Tier",
    course: "Course",
    year: "Year",
    grade_trend: "Grade Trend",
    attendance: "Attendance %",
    pattern: "Payment Pattern",
    timing: "Payment Timing",
    on_time_pct: "On-time %",
    payment_count: "Payment Count",
    payer: "Payer",
    loan_detected: "Loan Detected",
    repos: "Repositories",
    stars: "Stars",
    followers: "Followers",
    following: "Following",
    languages: "Languages",
    recent_activity: "Recent Activity",
    subscribers: "Subscribers",
    karma: "Karma",
    video_count: "Videos",
    views: "Total Views",
    monthly_spend_avg: "Avg Monthly Spend",
    spend_trend_6mo: "6-mo Spend Trend",
    essentials_pct: "Essentials %",
    file_name: "File",
    file_size: "Size",
};

function humanizeKey(key) {
    if (KEY_LABELS[key]) return KEY_LABELS[key];
    return key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatValue(value) {
    if (value === null || value === undefined || value === "") return "—";
    // Treat placeholder/unknown strings from the scraper as missing
    if (typeof value === "string") {
        const norm = value.trim().toLowerCase().replace(/^@/, "");
        if (norm === "unknown" || norm === "null" || norm === "none" || norm === "") return "—";
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) {
        if (value.length === 0) return "—";
        if (typeof value[0] === "object") return `${value.length} item${value.length === 1 ? "" : "s"}`;
        return value.join(", ");
    }
    if (typeof value === "object") {
        // Render small objects inline; large ones get a count.
        const keys = Object.keys(value);
        if (keys.length === 0) return "—";
        if (keys.length <= 4) {
            return keys.map((k) => `${humanizeKey(k)}: ${formatValue(value[k])}`).join(" · ");
        }
        return `${keys.length} fields`;
    }
    if (typeof value === "number") {
        if (Number.isInteger(value)) return value.toLocaleString("en-IN");
        return value.toFixed(2);
    }
    return String(value);
}

function formatDate(value) {
    if (!value) return null;
    try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return null;
        return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Kolkata" });
    } catch {
        return null;
    }
}

function groupForProvider(provider) {
    for (const [key, group] of Object.entries(SIGNAL_GROUPS)) {
        if (group.providers.includes(provider)) return key;
    }
    return "identity";
}

function EvidenceCard({ item, theme }) {
    const sourceBadge = SOURCE_BADGES[item.source_type] || SOURCE_BADGES.manual;
    const fetched = formatDate(item.fetched_at);
    const dataEntries = Object.entries(item.data || item.processed_data || item.raw_data || {})
        .filter(([key]) => !key.startsWith("_"));

    const cardClass = theme === "light"
        ? "rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
        : "rounded-lg border border-white/10 bg-white/[0.03] p-4";
    const titleClass = theme === "light" ? "text-slate-900" : "text-text-primary";
    const subtleClass = theme === "light" ? "text-slate-500" : "text-text-secondary";
    const labelClass = theme === "light" ? "text-slate-500" : "text-text-secondary";
    const valueClass = theme === "light" ? "text-slate-800 font-medium" : "text-text-primary font-medium";

    return (
        <div className={cardClass}>
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <p className={`font-semibold capitalize text-sm ${titleClass}`}>{item.provider || "Unknown provider"}</p>
                    {item.user_facing_label && (
                        <p className={`text-xs mt-0.5 ${subtleClass}`}>{item.user_facing_label}</p>
                    )}
                </div>
                <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${sourceBadge.color}`}>
                        {sourceBadge.label}
                    </span>
                    {typeof item.confidence === "number" && (
                        <span className={`text-[10px] font-mono ${subtleClass}`}>
                            <ShieldCheck size={10} className="inline mr-1" />
                            {Math.round(item.confidence * 100)}% conf
                        </span>
                    )}
                </div>
            </div>

            {dataEntries.length > 0 ? (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                    {dataEntries.map(([key, value]) => (
                        <div key={key} className="flex items-baseline gap-2 min-w-0">
                            <dt className={`shrink-0 ${labelClass}`}>{humanizeKey(key)}:</dt>
                            <dd className={`truncate ${valueClass}`} title={formatValue(value)}>{formatValue(value)}</dd>
                        </div>
                    ))}
                </dl>
            ) : (
                <p className={`text-xs italic ${subtleClass}`}>No detail captured.</p>
            )}

            {(fetched || item.consent_scope) && (
                <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 pt-3 border-t text-[11px] ${
                    theme === "light" ? "border-slate-100 text-slate-500" : "border-white/5 text-text-secondary"
                }`}>
                    {fetched && (
                        <span className="inline-flex items-center gap-1">
                            <Calendar size={11} /> {fetched}
                        </span>
                    )}
                    {item.consent_scope && (
                        <span className="inline-flex items-center gap-1">
                            <ShieldCheck size={11} /> {item.consent_scope.replace(/_/g, " ")}
                        </span>
                    )}
                </div>
            )}
        </div>
    );
}

export default function EvidenceSnapshotView({ evidence = [], theme = "dark", emptyMessage = "No evidence captured at the time of this score." }) {
    if (!evidence || evidence.length === 0) {
        return (
            <p className={`text-sm italic ${theme === "light" ? "text-slate-500" : "text-text-secondary"}`}>
                {emptyMessage}
            </p>
        );
    }

    // Group by signal category.
    const grouped = {};
    for (const item of evidence) {
        const groupKey = groupForProvider(item.provider);
        if (!grouped[groupKey]) grouped[groupKey] = [];
        grouped[groupKey].push(item);
    }

    // Build a flat list of all connected providers for the summary strip
    const allProviders = evidence.map(e => e.provider).filter(Boolean);

    // Render in stable order matching SIGNAL_GROUPS.
    return (
        <div className="space-y-6">
            {/* Platform summary strip — shows all connected providers at a glance so
                reviewers don't miss Developer-section platforms (GitHub/LinkedIn) when
                scrolled to the Social section */}
            {allProviders.length > 0 && (
                <div className={`flex flex-wrap items-center gap-2 p-3 rounded-lg text-xs ${
                    theme === "light"
                        ? "bg-slate-50 border border-slate-200"
                        : "bg-white/[0.03] border border-white/5"
                }`}>
                    <span className={`font-semibold uppercase tracking-wider mr-1 ${
                        theme === "light" ? "text-slate-500" : "text-text-secondary"
                    }`}>Connected:</span>
                    {Object.entries(SIGNAL_GROUPS).map(([groupKey, group]) => {
                        const groupItems = (grouped[groupKey] || []);
                        if (!groupItems.length) return null;
                        const Icon = group.icon;
                        return groupItems.map(item => (
                            <span key={`${item.provider}-pill`}
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium capitalize ${
                                    theme === "light"
                                        ? "bg-white border-slate-200 text-slate-700"
                                        : "bg-white/[0.05] border-white/10 text-text-primary"
                                }`}
                            >
                                <Icon size={10} style={{ color: group.color }} />
                                {item.provider}
                            </span>
                        ));
                    })}
                </div>
            )}
            {Object.entries(SIGNAL_GROUPS).map(([groupKey, group]) => {
                const items = grouped[groupKey];
                if (!items || items.length === 0) return null;
                const Icon = group.icon;
                const headingClass = theme === "light"
                    ? "text-slate-700 border-slate-200"
                    : "text-text-primary border-white/10";
                return (
                    <section key={groupKey}>
                        <h4 className={`flex items-center gap-2 text-sm font-semibold uppercase tracking-wider mb-3 pb-2 border-b ${headingClass}`}>
                            <Icon size={14} style={{ color: group.color }} />
                            {group.label}
                            <span className={`ml-auto text-[11px] font-normal normal-case tracking-normal ${
                                theme === "light" ? "text-slate-500" : "text-text-secondary"
                            }`}>
                                {items.length} source{items.length === 1 ? "" : "s"}
                            </span>
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {items.map((item, idx) => (
                                <EvidenceCard key={`${item.provider}-${idx}`} item={item} theme={theme} />
                            ))}
                        </div>
                    </section>
                );
            })}
        </div>
    );
}
